// Sprint 6 (Phase 1) — Founder-mode tool rendering catalog.
//
// Tools render with two layers:
//   1. Founder layer (default): one humanized line + status pill. Built from
//      `tool_use.input.description` (populated by the model for free) with
//      a per-tool fallback heuristic in `verb-map.ts` for the unlikely case
//      where the model omits it. NEVER includes raw shell commands, raw
//      paths beyond the basename, raw JSON, or raw stack traces.
//   2. Developer layer: existing collapsibles (`ToolCallBlock`, etc.) revealed
//      via the per-message `▽` expander or the global "Show developer
//      details" Settings toggle. Off by default per founder-first vision.
//
// `toFounderRow` is the pure derivation: given a `tool_use` message and its
// optional `tool_result`, returns the data the `FounderToolRow` component
// needs to render. Used by both the row component and the aggregation pass
// in Phase 2.

import type { Message } from '../../types';
import { asToolResult, asToolUse } from '../messages';
import { fallbackHeadline, type IconName } from './verb-map';

export type FounderRowStatus = 'running' | 'ok' | 'error' | 'interrupted';

// Sprint orphan-tool-rows-honest (D013-EC-147, spec D1/D3) — context for
// the plausibly-live rule. An unpaired tool_use may claim "running" ONLY
// while (a) the agent's derived task-scoped `working` signal (sprint
// working-signal-ssot) is true AND (b) the row's OWN turn has not closed.
//
// `turnClosedAfter` — a turn-closing system event (`result` / `exit` /
// the `turn_boundary` that opens the NEXT turn) appears after this row in
// the stream. Then the row's turn is provably over and its result will
// never arrive, regardless of whether the agent is working on something
// newer.
//
// Gate-2 correction (cycle 1): the first draft used "a later USER message
// exists" as the staleness proxy. That premise is false — the founder can
// send mid-turn (stream-json queues the input; cursor-ux-mirror §2.1
// requires it) and the row persists BEFORE that user row, so a genuinely
// executing tool would have flipped to "interrupted" the instant Santi
// typed. Turn closure is the real signal and needs no proxy.
//
// Ctx omitted (legacy call sites / tests) → pre-sprint behavior: unpaired
// = running. Every production call site passes it.
export interface RowLivenessCtx {
  agentWorking?: boolean;
  turnClosedAfter?: boolean;
}

// The ONE home of the plausibly-live rule (spec D3). `aggregate.ts`'s
// runningCount and every row surface derive from this — do not re-derive
// liveness from bare `!toolResult` pairing anywhere else.
export function isPlausiblyLive(ctx?: RowLivenessCtx): boolean {
  if (!ctx) return true;
  return (ctx.agentWorking ?? true) && !(ctx.turnClosedAfter ?? false);
}

export interface FounderRow {
  iconName: IconName;
  headline: string;
  status: FounderRowStatus;
  toolUseId: string;
  rawTool: string;
  inputs: Record<string, unknown>;
  // Set when a tool_result is present so the developer-mode reveal can show
  // the raw text. `undefined` while the tool is still running.
  result: unknown;
  isError: boolean;
}

const SHELL_TOKEN_RE = /(&&|\|\||;|>|>>|<|`|\$\(|\) *$)/;

// Read the model-supplied `description` from a tool_use input. Returns null
// when the field is missing, empty, or contains shell-like tokens — the
// founder layer must never leak the raw command.
function safeDescription(input: Record<string, unknown>): string | null {
  const v = input.description;
  if (typeof v !== 'string') return null;
  const trimmed = v.trim();
  if (trimmed.length === 0) return null;
  // Belt-and-suspenders: if the model ever surfaces the raw bash command in
  // `description` (it shouldn't), fall back to the heuristic so the founder
  // layer never displays shell tokens.
  if (SHELL_TOKEN_RE.test(trimmed)) return null;
  return trimmed;
}

export function toFounderRow(
  toolUseMessage: Message,
  toolResultMessage?: Message,
  ctx?: RowLivenessCtx,
): FounderRow {
  const tu = asToolUse(toolUseMessage);
  const description = safeDescription(tu.input);
  const fallback = fallbackHeadline(tu.name, tu.input);
  const headline = description ?? fallback.headline;
  const tr = toolResultMessage ? asToolResult(toolResultMessage) : null;
  const isError = tr?.is_error === true;
  // D013-EC-147 — an unpaired row is "running" only while plausibly live;
  // otherwise it renders as a quiet interrupted row (no ticking timer over
  // a process that died mid-tool).
  const status: FounderRowStatus = !tr
    ? isPlausiblyLive(ctx)
      ? 'running'
      : 'interrupted'
    : isError
      ? 'error'
      : 'ok';
  return {
    iconName: fallback.iconName,
    headline,
    status,
    toolUseId: tu.tool_use_id,
    rawTool: tu.name,
    inputs: tu.input,
    result: tr ? tr.content : undefined,
    isError,
  };
}
